import { Type } from '@sinclair/typebox';
import { PoolSchemaExtended } from '../pool/index.js';
import { SupportedDexSchema } from '../supported-dex.js';
import { SupportedNetworkSchema } from '../supported-network.js';
/**
 * Base Swap Settings
 */
export const BaseSwapSettingsSchema = Type.Object({
    slippage: Type.Number({
        description: 'Slippage in %',
        maximum: 100,
        minimum: 0,
    }),
    deadlineMs: Type.Number({
        description: 'Deadline in seconds',
    }),
}, {
    title: 'BaseSwapSettingsSchema',
});
export const BaseSvmSwapSettingsSchema = Type.Object({
    ...BaseSwapSettingsSchema.properties,
    jitoEnabled: Type.Boolean({
        description: 'Only for SOL: enable jito',
    }),
    jitoTip: Type.Number({
        description: 'Only for SOL: Jito tip in SOL',
        minimum: 0.0002,
    }),
    priorityFee: Type.Number({
        description: 'Only for SOL: Priority fee in SOL',
        minimum: 0.0001,
    }),
});
export const BaseEvmSwapSettingsSchema = Type.Object({
    ...BaseSwapSettingsSchema.properties,
    gasPrice: Type.Optional(Type.Number({
        description: 'Gas price in ETH',
    })),
    maxGasPrice: Type.Optional(Type.Number({
        description: 'Max gas price in ETH',
    })),
    maxPriorityFeePerGas: Type.Optional(Type.Number({
        description: 'Max priority fee in ETH',
    })),
    antiMev: Type.Optional(Type.Boolean({
        description: 'Only for ETH: enable anti-mev (MEV protection)',
    })),
});
export const BaseEvmLimitSettingsSchema = Type.Object({
    ...BaseSwapSettingsSchema.properties,
    antiMev: Type.Boolean({
        description: 'Only for ETH: enable anti-mev',
    }),
    maxGasPrice: Type.Optional(Type.Number({
        description: 'Max gas price in ETH',
    })),
    maxPriorityFeePerGas: Type.Optional(Type.Number({
        description: 'Max priority fee in ETH',
    })),
});
export const BaseTonSwapSettingsSchema = Type.Object({
    ...BaseSwapSettingsSchema.properties,
    gasPrice: Type.Number({
        description: 'Gas price in TON',
    }),
});
export const QUICK_SWAP_SECTIONS = [
    'exploreFeeds',
    'trendingTable',
    'cookingTable',
    'newPairsTable',
    'walletsTracker',
];
export const QuickSwapSectionSchema = Type.Union(QUICK_SWAP_SECTIONS.map((section) => Type.Literal(section)), { title: 'QuickSwapSectionSchema' });
export const PRESET_COUNT = 5;
export const DEFAULT_PRESET_EMOJIS = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣'];
export const PRESET_ZONES = ['regular', 'instant', 'limit'];
export const PresetZoneSchema = Type.Union(PRESET_ZONES.map((zone) => Type.Literal(zone)), { title: 'PresetZoneSchema' });
export const PresetSwapSideSchema = Type.Union([Type.Literal('buy'), Type.Literal('sell')], { title: 'PresetSwapSideSchema' });
const ZoneSchema = Type.Object({
    buy: Type.Number({ description: 'Buy amount in native currency' }),
    sell: Type.Number({
        description: 'Sell amount as % of balance (0..100)',
        minimum: 0,
        maximum: 100,
    }),
});
const quickSwapBySectionSchema = Type.Object(Object.fromEntries(QUICK_SWAP_SECTIONS.map((section) => [section, ZoneSchema])));
const buildPresetSchema = (settings) => Type.Object({
    name: Type.String({ description: 'Preset name' }),
    emoji: Type.String({ description: 'Preset emoji' }),
    ...settings.properties,
    regular: ZoneSchema,
    instant: ZoneSchema,
    limit: ZoneSchema,
});
export const SvmPresetSchema = buildPresetSchema(BaseSvmSwapSettingsSchema);
export const EvmPresetSchema = buildPresetSchema(BaseEvmSwapSettingsSchema);
export const TonPresetSchema = buildPresetSchema(BaseTonSwapSettingsSchema);
const buildSwapSettingsSchema = (preset, title) => Type.Object({
    presets: Type.Array(preset, {
        minItems: PRESET_COUNT,
        maxItems: PRESET_COUNT,
    }),
    activePreset: Type.Integer({
        minimum: 0,
        maximum: PRESET_COUNT - 1,
        default: 0,
        description: 'Index of the active preset (synced across devices)',
    }),
    quickAmounts: quickSwapBySectionSchema,
}, { title });
export const SvmSwapSettingsSchema = buildSwapSettingsSchema(SvmPresetSchema, 'SvmSwapSettingsSchema');
export const EvmSwapSettingsSchema = buildSwapSettingsSchema(EvmPresetSchema, 'EvmSwapSettingsSchema');
export const TonSwapSettingsSchema = buildSwapSettingsSchema(TonPresetSchema, 'TonSwapSettingsSchema');
export const TradeSettingsUnionSchema = Type.Union([
    BaseSvmSwapSettingsSchema,
    BaseEvmSwapSettingsSchema,
    BaseTonSwapSettingsSchema,
], {
    title: 'TradeResponseSchema',
});
export const TradeSwapStatusSchema = Type.Union([
    Type.Literal('active'),
    Type.Literal('failed'),
    Type.Literal('finished'),
]);
export const TradeSwapDetailsSchema = Type.Object({
    amountIn: Type.String(),
    amountInUsd: Type.String(),
    amountOut: Type.String(),
    amountOutUsd: Type.String(),
    tokenPriceInUsd: Type.String(),
    tokenPriceInNative: Type.String(),
    marketCap: Type.String(),
});
export const TradeSwapRollingStatsSchema = Type.Object({
    averageBuyMarketCap: Type.String(),
    averageSellMarketCap: Type.String(),
    averageBuyPrice: Type.String(),
    averageSellPrice: Type.String(),
    allTimeTokenAmount: Type.String(),
});
export const SwapSchema = Type.Object({
    id: Type.String(),
    delugeId: Type.String(),
    network: SupportedNetworkSchema,
    walletId: Type.String({
        description: 'Wallet ID which doing order',
    }),
    walletAddress: Type.String({
        description: 'Wallet address',
    }),
    type: Type.Union([
        Type.Literal('buy'),
        Type.Literal('sell'),
        Type.Literal('swap'),
    ]),
    tokenIn: Type.String(),
    tokenOut: Type.String(),
    path: Type.Optional(Type.Array(Type.String(), {
        description: 'Token route: [tokenIn, ...intermediate, tokenOut]. Set for type=swap.',
    })),
    pairAddress: Type.Optional(Type.String({
        description: 'Not set for type=swap (multihop, no single pair)',
    })),
    provider: Type.Optional(SupportedDexSchema),
    status: TradeSwapStatusSchema,
    txHash: Type.String(),
    swapDetails: Type.Optional(TradeSwapDetailsSchema),
    pnl: Type.String(),
    settings: TradeSettingsUnionSchema,
    limitOrderId: Type.Optional(Type.String({
        description: 'Limit order ID required for executing limit order',
    })),
    createdAt: Type.String(),
    updatedAt: Type.String(),
    rollingStats: TradeSwapRollingStatsSchema,
}, {
    title: 'TradeSwapSchema',
});
export const TradeSwapSchema = Type.Composite([
    SwapSchema,
    Type.Object({
        pairMeta: Type.Optional(PoolSchemaExtended),
    }),
]);
//# sourceMappingURL=swap.js.map